import { Chip, Stack, Typography } from '@mui/material';
import UploadedFilesView from '../components/UploadedFilesView';
import TooltipRemarkViewer from '../../../../components/datagrid/myworkspace/cro/TooltipRemarkViewer';
import {
  SKILLX_VERIFICATION_CHIP,
  SKILLX_VERIFICATION_STATUS,
} from './skillxAdmissionVerification';

/**
 * SkillX admission verification — GlobalTable headers.
 *
 * One row is one SkillX enrolment. Every renderCell reads `row`, never
 * `value`: GlobalTable substitutes an em dash for falsy cells before
 * renderCell sees them. Columns with a renderCell also have a valueGetter, so
 * the CSV export gets plain text instead of what is drawn.
 *
 * Widths: a header cell spends ~70px on padding and the freeze pin before its
 * title, so a column is sized to its title, not just its values.
 */

const fmtDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** "12 Mar 2001 (24)". Age is worked out from the DOB; without one, the lead's age. */
const fmtDobAge = (row) => {
  const dob = fmtDate(row.dob);
  if (dob) return row.age !== null && row.age !== undefined ? `${dob} (${row.age})` : dob;
  return row.age ? `(${row.age})` : '';
};

const fmtMoney = (amount) =>
  amount === null || amount === undefined ? '' : `₹${Number(amount).toLocaleString('en-IN')}`;

export const skillxAdmissionVerificationTableHead = [
  { field: 'sl_no', headerName: 'Sl No', width: 110 },
  {
    field: 'student_name',
    headerName: 'Student Name',
    width: 220,
    align: 'left',
    renderCell: ({ row }) => <TooltipRemarkViewer value={row.student_name} />,
  },
  { field: 'contact', headerName: 'Phone Number', width: 170 },
  { field: 'whatsapp', headerName: 'WhatsApp Number', width: 190 },
  {
    field: 'admn_no',
    headerName: 'Admission No',
    width: 165,
    renderCell: ({ row }) => (
      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{row.admn_no}</Typography>
    ),
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 240,
    align: 'left',
    renderCell: ({ row }) => <TooltipRemarkViewer value={row.email || '—'} />,
  },
  {
    field: 'dob',
    headerName: 'DOB (Age)',
    width: 160,
    valueGetter: ({ row }) => fmtDobAge(row),
  },
  { field: 'qualification', headerName: 'Qualification', width: 150 },
  { field: 'occupation', headerName: 'Occupation', width: 180, align: 'left' },
  // EMI / Fully paid / Partially paid / Not paid, worked out by the server.
  { field: 'payment_plan', headerName: 'Payment Mode', width: 170 },
  {
    field: 'total_fees',
    headerName: 'Total Fees',
    width: 140,
    valueGetter: ({ row }) => fmtMoney(row.total_fees),
  },
  {
    field: 'amount_paid',
    headerName: 'Amount Paid',
    width: 170,
    valueGetter: ({ row }) =>
      row.is_emi && row.emi_processing_fee
        ? `${fmtMoney(row.amount_paid)} + ${fmtMoney(row.emi_processing_fee)} EMI fee`
        : fmtMoney(row.amount_paid),
    renderCell: ({ row }) => (
      <Stack spacing={0.2} sx={{ py: 0.5 }}>
        <Typography sx={{ fontSize: 13.5, lineHeight: 1.3 }}>{fmtMoney(row.amount_paid)}</Typography>
        {row.is_emi && row.emi_processing_fee > 0 && (
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.3 }}>
            + {fmtMoney(row.emi_processing_fee)} EMI processing fee
          </Typography>
        )}
      </Stack>
    ),
  },
  { field: 'course_name', headerName: 'Course Name', width: 220, align: 'left' },
  { field: 'batch_name', headerName: 'Batch', width: 200, align: 'left' },
  { field: 'class_mode', headerName: 'Mode of Class', width: 170 },
  {
    field: 'screenshots',
    headerName: 'Payment Screenshot',
    width: 190,
    valueGetter: ({ row }) => (row.screenshots || []).map((s) => s.file).join(', '),
    renderCell: ({ row }) => (
      <UploadedFilesView
        title={`Payment screenshots — ${row.student_name}`}
        files={(row.screenshots || []).map((s) => ({
          file: s.file,
          caption: `${fmtMoney(s.amount)}${s.payment_mode ? ` · ${s.payment_mode}` : ''}`,
        }))}
      />
    ),
  },
  { field: 'admission_done_by', headerName: 'Admission Done By', width: 200, align: 'left' },
  {
    field: 'purchase_date',
    headerName: 'Purchase Date',
    width: 170,
    valueGetter: ({ row }) => fmtDate(row.purchase_date),
  },
  {
    field: 'verification_remarks',
    headerName: 'Student Remark',
    width: 260,
    align: 'left',
    renderCell: ({ row }) => <TooltipRemarkViewer value={row.verification_remarks || '—'} />,
  },
  {
    field: 'verification_status_label',
    headerName: 'Status',
    width: 150,
    renderCell: ({ row }) => {
      const chip = SKILLX_VERIFICATION_CHIP[row.verification_status] || {};
      return (
        <Chip
          size="small"
          label={row.verification_status_label}
          sx={{ fontWeight: 700, fontSize: 12, color: chip.color, bgcolor: chip.bg }}
        />
      );
    },
  },
  {
    // Only a row the learner reported can be marked verified; a pending row is
    // still the learner's to check.
    field: 'mark_verified',
    headerName: 'Action',
    type: 'action',
    actionType: 'edit',
    actionKey: 'MARK_VERIFIED',
    buttonName: 'Mark verified',
    icon: 'solar:check-circle-bold-duotone',
    width: 160,
    isDisabled: ({ row }) => row.verification_status !== SKILLX_VERIFICATION_STATUS.CRO_NOTIFIED,
  },
];
